import { NarrativeDevelopmentSchema } from "./narrative-arc.js";
import { z } from "zod";
import { MechanicalNoteSchema, ProseSchema } from "./prose.js";
import { DispositionSchema, DocumentKindSchema, FactIdSchema, FidelitySchema, ReliabilitySchema } from "./primitives.js";
import { ApproachSchema, ExperienceLevelSchema } from "./learner.js";
/**
 * P0 shared state block (docs/prompt_pack.md P0), re-injected every turn.
 * Derived exactly from the P0 JSON, plus the two extensions this task names
 * explicitly: `documents_delivered` and `dataroom_channels_known`.
 *
 * NOTE (docs/RECONCILIATION.md): P0's own example data has two fidelity gaps
 * this schema does NOT paper over:
 *   1. One example NPC's disposition is "eager, over-shares" — free text that
 *      does not fit the closed, ordered Disposition enum this task mandates
 *      in section 1. `npcs[].disposition` below is typed as the strict enum;
 *      that example value is simply unparseable against it, which is
 *      reported rather than silently loosened.
 *   2. `npcs[].relationship` (NpcRelationship) is used elsewhere in this
 *      package (NarratorView, CaseBible) but is NOT added to StateBlock here,
 *      because the task's extension instruction for StateBlock names only
 *      `documents_delivered` and `dataroom_channels_known` — adding a third,
 *      unrequested field would be exactly the kind of invention the task
 *      instructs against. Flagged rather than added.
 */
const RubricEstimateSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).nullable();
const StateBlockClockSchema = z.strictObject({
    name: z.string().min(1),
    filled: z.number().int().min(0),
    size: z.number().int().min(1),
    trend: z.string().optional(),
    threshold_to_present: z.number().int().min(0).optional(),
});
const StateBlockResourcesSchema = z.strictObject({
    time_days_left: z.number().int().min(0),
    political_capital: z.number().int().min(0),
    analysts: z.number().int().min(0),
});
const StateBlockFactSchema = z.strictObject({
    id: FactIdSchema,
    text: ProseSchema,
    source: z.string().min(1),
    reliability: ReliabilitySchema,
});
const StateBlockHypothesisSchema = z.strictObject({
    turn: z.number().int().min(0),
    /** The learner's own words — never Prose-filtered. See prose.ts's module doc for why. */
    text: z.string().min(1),
    /** System-generated standing commentary (e.g. "partially contradicted by F019"), fed to the Narrator to translate into fiction. */
    status: MechanicalNoteSchema,
});
const StateBlockNpcSchema = z.strictObject({
    name: z.string().min(1),
    role: z.string().min(1),
    disposition: DispositionSchema,
    private_agenda_visible_to_learner: z.boolean(),
});
const StateBlockRulingSchema = z.strictObject({
    turn: z.number().int().min(0),
    ruling: ProseSchema,
    final: z.boolean(),
});
const StateBlockDeliveredDocumentSchema = z.strictObject({
    id: z.string().min(1),
    kind: DocumentKindSchema,
    fidelity: FidelitySchema,
    /**
     * The document's authored title, as the client names it. Optional because
     * a state block built before 2026-09-09 had none, and the narrator then saw
     * "financial table (doc_five_quarter_pl_summary)" for everything on the
     * desk — half the canon errors in the calibration playthrough were the
     * narrator not knowing what the learner already held.
     */
    title: z.string().min(1).optional(),
});
/**
 * Deliberately NOT the whole LearnerProfile.
 *
 * `target_firms`, `self_identified_gaps` and `extracted_from_transcript` are
 * the learner's own words about themselves — personal data with no bearing on
 * how hard an interview should be, and Law 5 says what reaches a model provider
 * should be the minimum that does the job. What an Adjudicator can legitimately
 * weigh is how this person tends to work and how far along they are.
 */
const StateBlockLearnerProfileSchema = z.strictObject({
    /** Up to two inferred Approaches (Law 6 — inferred from evidence, never chosen from a list); none while the profile has no words to back one. */
    approaches: z.array(ApproachSchema).max(2),
    experience_level: ExperienceLevelSchema,
    /** How many engagements this learner has actually finished. Context for calibration, not a threshold. */
    completed_cases: z.number().int().min(0),
    /**
     * The six rubric-dimension estimates, 1-5, most recent median per dimension.
     * Null for a dimension with no scored history — and ALL null until the
     * learner has finished enough cases for `sufficient_n` (three), because a
     * trend drawn from one session is noise, and adjusting difficulty on noise is
     * worse than not adjusting it.
     */
    skill_estimates: z.strictObject({
        structure: RubricEstimateSchema,
        quantitative: RubricEstimateSchema,
        judgment: RubricEstimateSchema,
        communication: RubricEstimateSchema,
        ambiguity_tolerance: RubricEstimateSchema,
        calibration: RubricEstimateSchema,
    }),
});
export const StateBlockSchema = z.strictObject({
    turn: z.number().int().min(0),
    day: z.number().int().min(0),
    day_limit: z.number().int().min(1),
    case_id: z.string().min(1),
    client: z.string().min(1),
    engagement_question: ProseSchema,
    resources: StateBlockResourcesSchema,
    clocks: z.array(StateBlockClockSchema),
    facts_known_to_learner: z.array(StateBlockFactSchema),
    facts_available_unrequested: z.array(FactIdSchema),
    hypotheses_on_record: z.array(StateBlockHypothesisSchema),
    npcs: z.array(StateBlockNpcSchema),
    open_threads: z.array(ProseSchema),
    immutable_rulings: z.array(StateBlockRulingSchema),
    /** Authored world developments already admitted by code, in order; no future arc or solution. */
    narrative_developments: z.array(NarrativeDevelopmentSchema).optional(),
    /** Extension (this task, section 3): documents delivered so far — id + kind + fidelity. */
    documents_delivered: z.array(StateBlockDeliveredDocumentSchema),
    /** Extension (this task, section 3): data-room channel names the learner knows about. */
    dataroom_channels_known: z.array(z.string().min(1)),
    /**
     * THE LEARNER'S CROSS-SESSION SKILL PROFILE (Law 6, ADR 0006).
     *
     * The Adjudicator prompt has always instructed the model to weigh "the
     * learner's chosen Approach" and "the learner's persisted skill profile
     * (ADR 0006)", with a worked example modifier — and the model was never
     * given either. Across five checks in a live session, not one modifier
     * referenced the profile, because there was nothing to reference. This
     * field is what closes that.
     *
     * A MODIFIER REASON, NEVER A GATE. Law 6 is explicit: difficulty adapts,
     * access does not. This value may only ever appear as one more entry in a
     * CheckSpec's `modifiers`, already capped at a net ±6 (ADR 0005) — so the
     * worst it can do is move a DC by six. It may not change `requires_roll`,
     * `position` or `effect`, may not refuse an intent, and may not remove
     * anything from the learner's menu. `deriveLearnerViewInPlay` and
     * `deriveNarratorView` do not read it, and there is a test asserting their
     * output is unchanged by its presence.
     *
     * Null before onboarding is confirmed, and for a guest who never did it.
     */
    learner_profile: StateBlockLearnerProfileSchema.nullable(),
});
//# sourceMappingURL=state-block.js.map